import { format, isToday } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DatePickerProps {
  date?: Date;
  onSelect: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  disabledDates?: (date: Date) => boolean;
}

export function DatePicker({ date, onSelect, placeholder = 'Pick a date', className, showClearButton = false, disabledDates }: DatePickerProps) {
  const formatDate = (selectedDate: Date) => {
    if (isToday(selectedDate)) {
      return 'Today';
    }
    return format(selectedDate, 'MMM d, yyyy');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn('justify-start text-left font-normal', !date && 'text-muted-foreground', className)}
        >
          <CalendarIcon className='mr-2 h-4 w-4' />
          <span className="flex-1">
            {date ? formatDate(date) : placeholder}
          </span>
          {showClearButton && (
            <X
              className={cn('ml-2 h-4 w-4 rounded-sm p-0.5', date ? 'hover:bg-gray-100 cursor-pointer opacity-100' : 'opacity-0 pointer-events-none')}
              onClick={handleClear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0'>
        <Calendar mode='single' selected={date} onSelect={onSelect} disabled={disabledDates} initialFocus />
      </PopoverContent>
    </Popover>
  );
}
